<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router';
import StepHeader from '../../components/StepHeader/StepHeader.vue';
import TeacherPreviewBanner from 'shared/components/TeacherPreviewBanner/TeacherPreviewBanner.vue';
import StepCard from '../../components/StepCard/StepCard.vue';
import StepLoadBanners from '../../components/StepLoadBanners/StepLoadBanners.vue';
import StepLinearProgress from '../../components/StepLinearProgress/StepLinearProgress.vue';
import StepInstructionsDialog from '../../components/StepInstructionsDialog/StepInstructionsDialog.vue';
import StepResultsDialog from '../../components/StepResultsDialog/StepResultsDialog.vue';
import CompletedStepBanner from 'shared/components/CompletedStepBanner/CompletedStepBanner.vue';
import BttsButton from 'src/shared/components/base/BTTSButton/BttsButton.vue';
import type { TermsOfTransition } from 'types/course/termsOfTransition';
import { useStepEngine } from '../../composables/useStepEngine';
import { useDrillSession } from '../../composables/useDrillSession';
import { useStepViewLifecycle } from '../../composables/useStepViewLifecycle';
import { useStepDialogs } from '../../composables/useStepDialogs';
import { useStepHeaderActions } from '../../composables/useStepHeaderActions';
import { useDrillComponents } from '../../composables/useDrillComponents';

const props = defineProps<{
  termsOfTransitions?: TermsOfTransition[];
  userSectionId?: number;
  nextSectionStepDocId?: string;
}>();

const emits = defineEmits<{
  'step:complete': [{ timeAccumulator: number; score: number }];
  'step:start': [dateTime: string];
  'action:completion': [];
  'step:skippedFilter': [];
}>();

const router = useRouter();
const route = useRoute();
const stepDocId = route.params.stepDocId as string;
const userStepId = route.params?.userStepId ? +route.params.userStepId : 0;
const userSectionDocId = route.params.userSectionDocId as string | undefined;

const engine = useStepEngine(stepDocId, userStepId, userSectionDocId);
const { isLoading, loadError, validationErrors, documentStepRepo } = engine;
const { isCompletedStep, isSkippedStep, stepDrillProps } = engine;

const session = useDrillSession({ engine });
const { drillTextContent, drillResult, isCheckingAnswer } = session;
const { playQuestion, resetStep } = session;

const lifecycle = useStepViewLifecycle({
  ...engine,
  ...session,
  emit: emits,
  stepDocId,
  userSectionDocId,
  router,
  route,
});

const dialogs = useStepDialogs({ ...engine, ...session });

useStepHeaderActions({
  ...engine,
  stepDocId,
  nextSectionStepDocId: () => props.nextSectionStepDocId,
  onReset: async () => {
    await resetStep();
    await playQuestion();
  },
  onInstruction: () => dialogs.showInfo(),
  onHelp: () => session.getHelp(),
  onInterim: () => dialogs.viewResults(),
});

const { drillComponent, solveComponent, resultsComponent, infoComponent } =
  useDrillComponents(session.drillKind, engine.stepTypeCamelCase);

defineExpose({ resetStep, playQuestion, awaitSaves: engine.awaitSaves });
</script>

<template>
  <StepCard
    v-if="documentStepRepo"
    class="column col-grow no-wrap full-width full-height"
    flat
  >
    <TeacherPreviewBanner class="q-mb-sm" />
    <template #progress>
      <StepLinearProgress
        :value="engine.stepProgressPercent.value"
        :label="engine.stepProgressLabel.value"
      />
    </template>

    <template #header>
      <StepHeader
        class="text-dark q-py-md full-width"
        :is-completed-step="isCompletedStep"
        :error-count="engine.errorCount.value"
        :correct-count="engine.correctCount.value"
        :score="engine.score.value"
        :title="documentStepRepo.step_name"
        :subtitle="documentStepRepo.step_category"
        :category="documentStepRepo.step_category"
        v-bind="session.clockProps.value"
        :is-skipped-step="isSkippedStep"
      />
    </template>

    <template
      v-if="
        !isCompletedStep &&
        !isSkippedStep &&
        stepDrillProps.isQuestionHearingEnabled
      "
      #tts
    >
      <BttsButton
        :disable="isCheckingAnswer"
        class="q-mr-lg q-mt-sm"
        @click="async () => await playQuestion()"
      />
    </template>

    <template #drill>
      <component
        :is="drillComponent"
        v-if="!isCompletedStep && !isSkippedStep"
        v-model:drill-text-content="drillTextContent"
        v-bind="{ ...stepDrillProps, 'disable-actions': isCheckingAnswer }"
        @change="session.handleDrillResult"
      />
      <q-card-section
        v-else
        class="full-width"
      >
        <slot name="completionBanner">
          <CompletedStepBanner
            :action-btn-label="lifecycle.completeActionButtonLabel.value"
            @completion-action="lifecycle.handleCompletionAction"
          />
        </slot>
      </q-card-section>
    </template>

    <template
      v-if="!isCompletedStep"
      #solve
    >
      <component
        :is="solveComponent"
        v-bind="{
          ...engine.stepSolveProps.value,
          drillResult,
          'disable-actions': isCheckingAnswer,
        }"
        @selected-answer="session.checkSelectedAnswer"
      />
    </template>

    <template #error>
      <StepLoadBanners
        :load-error="loadError"
        :validation-errors="validationErrors"
        @retry="lifecycle.retryLoadStep"
        @back="router.back()"
      />
    </template>

    <StepInstructionsDialog
      v-model:is-open="dialogs.isInfoDialog.value"
      :info-component="infoComponent"
      :instructions="lifecycle.sanitizedInstructions.value"
      :drill="engine.stepDrill.value"
      :terms-of-transitions="termsOfTransitions"
      @hide="dialogs.infoDialogHideHandler"
    />

    <StepResultsDialog
      v-model:is-open="dialogs.isInterimResultsDialog.value"
      :results-component="resultsComponent"
      :current-step-results="engine.currentStepResults.value"
      :error-count="engine.errorCount.value"
      :correct-count="engine.correctCount.value"
      :time-accumulator="engine.timeAccumulator.value"
      :score="engine.score.value"
      :stack-source="engine.stackSource.value"
      :drill-type="engine.stepDrill.value?.drill_type?.[0]"
      :step-type-component="engine.filterStepType.value"
      @hide="dialogs.interimResultsDialogHideHandler"
    />
  </StepCard>
  <!-- The step never loaded: the banners still render (BUG-11, BUG-32). -->
  <StepLoadBanners
    v-else-if="!isLoading"
    :load-error="loadError"
    :validation-errors="validationErrors"
    is-not-found
    @retry="lifecycle.retryLoadStep"
    @back="router.back()"
    @home="router.push({ name: 'dashboard' })"
  />
</template>
